/**
 * Sorting a table by clicking its head.
 *
 * Kept here rather than in each table because the interesting part is the DEFAULT DIRECTION, and
 * getting that wrong makes a sortable column feel broken. Most columns want biggest-first — you
 * click "Titles" to see who has the most — but a few are the other way round: a best finish of 1
 * is better than 12, so that column opens ascending. Each header declares which it is, and a
 * second click still reverses it.
 *
 * Names sort alphabetically and by the name shown, not the id, so clicking "GM" gives A-Z as read.
 */

import { useState } from 'react';
import { managerShort } from '../domain/identity.js';

export interface Sort<T> {
  col: keyof T & string;
  dir: 'asc' | 'desc';
  set: (col: keyof T & string, defaultAsc: boolean) => void;
  rows: (rows: readonly T[]) => T[];
}

export function useSort<T>(
  initial: keyof T & string,
  initialDir: 'asc' | 'desc' = 'desc',
): Sort<T> {
  const [col, setCol] = useState<keyof T & string>(initial);
  const [dir, setDir] = useState<'asc' | 'desc'>(initialDir);

  return {
    col,
    dir,
    set: (next, defaultAsc) => {
      if (next === col) setDir(dir === 'asc' ? 'desc' : 'asc');
      else {
        setCol(next);
        setDir(defaultAsc ? 'asc' : 'desc');
      }
    },
    rows: (rows) => [...rows].sort((a, b) => {
      const x = (a as Record<string, unknown>)[col];
      const y = (b as Record<string, unknown>)[col];
      // A GM column holds an id but reads as a name, so it must sort the way it reads.
      const cmp = typeof x === 'string' && typeof y === 'string'
        ? (col === 'gm' ? managerShort(x).localeCompare(managerShort(y)) : x.localeCompare(y))
        : Number(x ?? 0) - Number(y ?? 0);
      return dir === 'asc' ? cmp : -cmp;
    }),
  };
}

export function SortableHead<T>(
  { sort, col, label, asc = false, text = false }:
  { sort: Sort<T>; col: keyof T & string; label: string; asc?: boolean; text?: boolean },
) {
  const on = sort.col === col;
  return (
    <th className={`${text ? '' : 'num'} sortable ${on ? 'on' : ''}`}
      aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => sort.set(col, asc)}>
        {label}
        <span className="caret" aria-hidden="true">{on ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
}
